import React from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography, Upload } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { CloseOutlined, ContainerOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createConfig } from '../../services/config';
import BundledEditor from '../../components/BundledEditor';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';

const itemName = 'Cấu hình';
const { Text, Title } = Typography;

const TYPE_OPTIONS = [
  { value: 1, label: 'Chuỗi' },
  { value: 2, label: 'JSON' },
  { value: 3, label: 'Ảnh' },
  { value: 4, label: 'HTML' },
  { value: 5, label: 'Slide' },
  { value: 6, label: 'Banner' },
  { value: 7, label: 'Thư mục ảnh' },
];

const STATUS_OPTIONS = [
  { value: 1, label: 'Hoạt động' },
  { value: 0, label: 'Không hoạt động' },
];

const CreateConfig = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const type = Form.useWatch('type', form);

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: createConfig,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Thêm mới ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleUploadImage = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) return false;
    const url = await uploadImageCDN(file, () => {}, 'toancohien_config');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ value: safeUrl });
    }
    return false;
  };

  const handleSubmitForm = (data) => {
    const payload = {
      key: data?.key,
      type: Number(data?.type || 1),
      title: data?.title,
      value: data?.value,
      note: data?.note,
      status: data?.status,
    };
    // If type is slide, convert slides array to JSON string
    if (Number(payload.type) === 5) {
      const slides = Array.isArray(data?.slides) ? data.slides : [];
      // basic validation: image required
      const invalid = slides.some((s) => !s?.image);
      if (invalid) {
        notification.error({ message: 'Mỗi slide cần có ảnh (image)!' });
        return;
      }
      // sort by order asc then normalize order
      const sorted = [...slides].sort((a, b) => (Number(a?.order || 0) - Number(b?.order || 0)) || 0)
        .map((s, i) => ({
          title: s?.title || '',
          image: s?.image || '',
          link: s?.link || '',
          order: i + 1,
        }));
      payload.value = JSON.stringify(sorted);
    }
    // If type is banner, convert banner group to JSON string
    if (Number(payload.type) === 6) {
      const bn = data?.banner || {};
      const banner = {
        image: bn?.image || '',
        link: bn?.link || '',
      };
      if (!banner.image) {
        notification.error({ message: 'Vui lòng chọn ảnh cho banner!' });
        return;
      }
      payload.value = JSON.stringify(banner);
    }
    // If type is album (7), serialize array of image links
    if (Number(payload.type) === 7) {
      const album = Array.isArray(data?.album) ? data.album.filter((u) => typeof u === 'string' && u.trim()) : [];
      payload.value = JSON.stringify(album);
    }
    // Optional: validate JSON if type=2
    if (Number(payload.type) === 2 && payload.value) {
      try {
        JSON.parse(payload.value);
      } catch (e) {
        notification.error({ message: 'Giá trị không phải JSON hợp lệ!' });
        return;
      }
    }
    mutateCreate(payload);
  };

  const renderValueField = () => {
    if (Number(type || 1) === 3) {
      return (
        <Form.Item name="value" label={<Text>Giá trị (URL ảnh)</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
          <Input
            placeholder="Nhập URL hoặc tải lên ảnh..."
            addonBefore={
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadImage} className="hover:cursor-pointer">
                <UploadOutlined /> Upload
              </Upload>
            }
          />
        </Form.Item>
      );
    }
    if (Number(type || 1) === 5) {
      return (
        <Form.List name="slides" initialValue={[]}>
          {(fields, { add, remove }, { move }) => {
            const handleUploadAt = async (file, namePath) => {
              const isValid = checkValidUpload(file);
              if (!isValid) return false;
              const url = await uploadImageCDN(file, () => {}, 'toancohien_config_slide');
              if (url) {
                const safeUrl = encodeURI(url);
                // antd v4 compatibility: update nested field via setFieldsValue on full object
                const slides = Array.isArray(form.getFieldValue('slides')) ? [...form.getFieldValue('slides')] : [];
                const idx = Array.isArray(namePath) ? Number(namePath[0]) : -1;
                if (idx >= 0) {
                  slides[idx] = { ...(slides[idx] || {}), image: safeUrl };
                  form.setFieldsValue({ slides });
                }
                notification.success({ message: 'Tải lên thành công!' });
              }
              return false;
            };
            return (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Text>Danh sách Slide</Text>
                  <Button type="dashed" onClick={() => add({ title: '', image: '', link: '', order: fields.length + 1 })}>Thêm slide</Button>
                </div>
                <div className="space-y-3">
                  {fields.map((field, idx) => {
                    const base = field.name;
                    return (
                      <div key={field.key} className="p-3 border rounded-md bg-white">
                        <div className="flex items-center gap-3 mb-2">
                          <Text strong>Slide #{idx + 1}</Text>
                          <Space className="ml-auto">
                            <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                          </Space>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Form.Item name={[base, 'title']} label={<Text>Tiêu đề</Text>}>
                            <Input placeholder="Nhập tiêu đề..." />
                          </Form.Item>
                          <Form.Item name={[base, 'order']} label={<Text>Thứ tự</Text>} initialValue={idx + 1}>
                            <Input type="number" min={1} placeholder="VD: 1" />
                          </Form.Item>
                          <Form.Item name={[base, 'link']} label={<Text>Đường dẫn</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
                            <Input placeholder="https://..." />
                          </Form.Item>
                          <Form.Item
                            name={[base, 'image']}
                            label={<Text>Hình ảnh <span className="text-red-700">*</span></Text>}
                            rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
                          >
                            <Input
                              placeholder="URL ảnh hoặc tải lên..."
                              addonBefore={
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadAt(file, [base, 'image'])} className="hover:cursor-pointer">
                                  <UploadOutlined /> Upload
                                </Upload>
                              }
                            />
                          </Form.Item>
                        </div>
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const imageUrl = form.getFieldValue(['slides', idx, 'image']);
                            return imageUrl ? (
                              <div className="w-full md:w-1/2">
                                <img
                                  src={imageUrl}
                                  alt="thumb"
                                  style={{ width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                                />
                              </div>
                            ) : null;
                          }}
                        </Form.Item>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        </Form.List>
      );
    }
    if (Number(type || 1) === 4) {
      return (
        <Form.Item name="value" label={<Text>Giá trị (HTML)</Text>} valuePropName="value" trigger="onEditorChange" getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}>
          <BundledEditor
            init={{
              license_key: 'gpl',
              height: 400,
              menubar: false,
              plugins: ['advlist','autolink','lists','link','image','charmap','preview','anchor','searchreplace','visualblocks','code','fullscreen','insertdatetime','media','table','help','wordcount'],
              toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | code | help',
            }}
          />
        </Form.Item>
      );
    }
    if (Number(type || 1) === 6) {
      const handleUploadBanner = async (file) => {
        const isValid = checkValidUpload(file);
        if (!isValid) return false;
        const url = await uploadImageCDN(file, () => {}, 'toancohien_config_banner');
        if (url) {
          const safeUrl = encodeURI(url);
          const banner = form.getFieldValue('banner') || {};
          form.setFieldsValue({ banner: { ...banner, image: safeUrl } });
          notification.success({ message: 'Tải lên thành công!' });
        }
        return false;
      };
      return (
        <div>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name={['banner', 'link']} label={<Text>Đường dẫn</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['banner', 'image']}
                label={<Text>Hình ảnh <span className="text-red-700">*</span></Text>}
                rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
              >
                <Input
                  placeholder="URL ảnh hoặc tải lên..."
                  addonBefore={
                    <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadBanner} className="hover:cursor-pointer">
                      <UploadOutlined /> Upload
                    </Upload>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const imageUrl = form.getFieldValue(['banner', 'image']);
                  return imageUrl ? (
                    <div className="w-full">
                      <img
                        src={imageUrl}
                        alt="banner-thumb"
                        style={{ width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                      />
                    </div>
                  ) : null;
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>
      );
    }
    if (Number(type || 1) === 7) {
      return (
        <Form.List name="album" initialValue={['']}>
          {(fields, { add, remove }) => {
            const handleUploadAlbumAt = async (file, index) => {
              const isValid = checkValidUpload(file);
              if (!isValid) return false;
              const url = await uploadImageCDN(file, () => {}, 'toancohien_config_album');
              if (url) {
                const safeUrl = encodeURI(url);
                const album = Array.isArray(form.getFieldValue('album')) ? [...form.getFieldValue('album')] : [];
                album[index] = safeUrl;
                form.setFieldsValue({ album });
                notification.success({ message: 'Tải lên thành công!' });
              }
              return false;
            };
            return (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Text>Danh sách ảnh</Text>
                  <Button type="dashed" onClick={() => add('')}>Thêm ảnh</Button>
                </div>
                {fields.map((field, idx) => (
                  <div key={field.key} className="p-3 border rounded-md bg-white">
                    <div className="flex items-center gap-3 mb-2">
                      <Text strong>Ảnh #{idx + 1}</Text>
                      <Space className="ml-auto">
                        <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                      </Space>
                    </div>
                    <Form.Item name={field.name} label={<Text>Đường dẫn ảnh</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
                      <Input
                        placeholder="URL ảnh hoặc tải lên..."
                        addonBefore={
                          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadAlbumAt(file, idx)} className="hover:cursor-pointer">
                            <UploadOutlined /> Upload
                          </Upload>
                        }
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const url = form.getFieldValue(['album', idx]);
                        return url ? (
                          <img src={url} alt="thumb" style={{ width: '100%', height: 'auto', maxHeight: 200, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }} />
                        ) : null;
                      }}
                    </Form.Item>
                  </div>
                ))}
              </div>
            );
          }}
        </Form.List>
      );
    }
    const placeholder = Number(type || 1) === 2 ? 'Nhập JSON hợp lệ...' : 'Nhập giá trị...';
    return (
      <Form.Item name="value" label={<Text>Giá trị</Text>}>
        <Input.TextArea placeholder={placeholder} autoSize={{ minRows: 3 }} />
      </Form.Item>
    );
  };

  return (
    <Form name="create-config" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item name="key" label={<Text>Key <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng nhập key' }]}>
            <Input placeholder="Nhập key cấu hình (duy nhất)..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="title" label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}>
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="type" label={<Text>Loại <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn loại' }]} initialValue={1}>
            <Select showSearch optionFilterProp="label" options={TYPE_OPTIONS} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="status" label={<Text>Trạng thái <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]} initialValue={1}>
            <Select showSearch optionFilterProp="label" options={STATUS_OPTIONS} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="note" label={<Text>Ghi chú</Text>}>
            <Input.TextArea placeholder="Ghi chú..." autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Col>

        <Col xs={24}>{renderValueField()}</Col>

        <Col xs={24}>
          <Row justify="end" style={{ marginTop: 10 }}>
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateConfig;
